"use client";

import * as React from "react";
import { Bell, LogOut, Monitor, Moon, Sun, User } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { UserAvatar } from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import Link from "next/link";
import { currentStageOrigin, useStageOrigin } from "@workspace/ui/lib/stage-url";
import { cn } from "@workspace/ui/lib/utils";

const authUrl = process.env.NEXT_PUBLIC_AUTH_URL || "https://auth.gaz.codes";

/**
 * Fallbacks only. The links below resolve against the stage the page is on
 * (`lib/stage-url.ts`): these env values are baked at build time, so a local
 * page built without them sent you to PROD, where an installed local PWA can
 * never enable push.
 *
 * Notifications must be web's origin, not the current app's: a push
 * subscription is scoped to the installed origin, and web is the one we
 * install. In that installed app there is no address bar, so this menu is the
 * only way to reach the page that enables push.
 */
const webFallback = authUrl.replace("//auth.", "//");

const THEMES = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
] as const;

/**
 * Light / dark / system, as one row of the account menu.
 *
 * Signed in, this is where the theme lives: the header's standalone toggle is
 * only shown to signed-out visitors, who have no menu (see `app-header.tsx`).
 *
 * Each choice is a menu item, so arrow keys reach it like any other row, with
 * the radio role it actually has. Choosing one keeps the menu open: the point
 * is to see the change, and closing would hide the control that made it.
 * The menu content only mounts once opened, after hydration, so next-themes
 * already knows the stored theme and no mounted guard is needed.
 */
function ThemeChoice() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1">
      <span className="text-sm">Theme</span>
      <div role="group" aria-label="Theme" className="flex gap-0.5 rounded-md bg-muted p-0.5">
        {THEMES.map(({ value, label, Icon }) => (
          <DropdownMenuItem
            key={value}
            role="menuitemradio"
            aria-checked={theme === value}
            aria-label={label}
            title={label}
            onSelect={(event) => {
              event.preventDefault();
              setTheme(value);
            }}
            className={cn(
              "justify-center p-1.5",
              theme === value
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground",
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
          </DropdownMenuItem>
        ))}
      </div>
    </div>
  );
}

export function AccountMenu() {
  const { user, authenticated, logout } = useAuth();

  // Build the login URL with a return_to pointing back at the current page, so
  // the auth app's useReturnTo() bounces the user here after sign-in instead of
  // stranding them on /profile. Computed in an effect (not at render) because
  // window.location.href isn't available during SSR — until it resolves we link
  // to bare /login, matching this component's existing post-mount swap pattern.
  const authOrigin = useStageOrigin("auth", authUrl);
  const webOrigin = useStageOrigin(undefined, webFallback);
  const [loginHref, setLoginHref] = React.useState(`${authUrl}/login`);
  React.useEffect(() => {
    const returnTo = encodeURIComponent(window.location.href);
    setLoginHref(`${currentStageOrigin("auth", authUrl)}/login?return_to=${returnTo}`);
  }, []);

  // useAuth is SSR-stable: cookieUser starts null on both SSR and first client
  // render, then hydrates in useEffect. So the very first render here is
  // always !authenticated → Sign in button. The avatar swaps in after the
  // mount effect fires. No skeleton placeholder needed — Sign-in → avatar is
  // a single, harmless swap, and there's never a hydration mismatch.
  if (!authenticated || !user) {
    return (
      <Link href={loginHref}>
        <Button size="sm" className="rounded-full">
          Sign in
        </Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <UserAvatar
            email={user.email}
            username={user.initials ? undefined : user.name}
            initials={user.initials}
            avatarUrl={user.avatar}
            backgroundColor={user.avatarBgColor}
            size="sm"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user.name}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href={`${authOrigin}/profile`}>
            <User className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`${webOrigin}/settings/notifications`}>
            <Bell className="mr-2 h-4 w-4" />
            Notifications
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <ThemeChoice />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
